import JSZip from "jszip";
import { IAirtableAttachment } from "@rogoag/airtable";
import axios from "axios";
import { AWS, FileResponseType } from "./aws";
import { CSV } from "@rogoag/shared-ts";

const DEFAULT_FILE_NAME = "rogo_files.zip";

/**
 * Operations for loading/downloading files from various sources.
 * "Downloading" refers to downloading files from the browser. "Loading" refers to loading files into memory.
 */
export namespace FileDownloader {
	export async function downloadFile(file: File, filename: string = "", zip: boolean = false) {
		await FileDownloader.downloadFiles([file], filename, zip);
	}

	export async function downloadFiles(files: File[], filename: string = "", zip: boolean = true) {
		if (zip) {
			const zip = new JSZip();
			files.forEach((file) => {
				zip.file(file.name, file);
			});
			await FileDownloader.downloadZip(zip, filename);
		} else {
			files.forEach((file) => {
				FileDownloader.downloadBlob(file, file.name);
			});
		}
	}

	interface GroupOfFiles {
		key: string;
		files: File[];
	}

	export async function downloadGroupsOfFiles(files: GroupOfFiles[], filename: string = "") {
		function addFilesToZip(files: File[], folderName: string) {
			const folder = zip.folder(folderName);
			files.forEach((file) => {
				folder?.file(file.name, file);
			});
		}

		const zip = new JSZip();
		files.forEach((file) => {
			addFilesToZip(file.files, file.key);
		});
		await FileDownloader.downloadZip(zip, filename);
	}

	export async function downloadAttachment(
		attachment: IAirtableAttachment,
		filename: string = "",
		zip: boolean = false,
		jobId: string = "",
	) {
		return await FileDownloader.downloadAttachments([attachment], filename, zip, false, jobId);
	}

	export async function downloadAttachments(
		attachments: IAirtableAttachment[],
		filename: string = "",
		zip: boolean = false,
		mergeCSV: boolean = false,
		jobId: string = "",
	) {
		if (attachments.length === 1) {
			await FileDownloader.downloadURL(attachments[0].url, filename || attachments[0].filename, zip);
		} else {
			if (mergeCSV) {
				const csvResults = await Promise.all(
					attachments.map(async (f) => {
						try {
							return (await FileDownloader.loadAttachment(f, jobId, "text")) as string;
						} catch {
							console.warn(`Failed to load CSV: ${f.filename}`);
							return null;
						}
					}),
				);
				const csvs = csvResults.filter((csv): csv is string => csv !== null && csv !== undefined);
				if (csvs.length === 0) throw new Error("All CSV downloads failed");
				const mergedCSV = CSV.merge(csvs);
				const blob = new Blob([mergedCSV], { type: "text/csv" });
				FileDownloader.downloadBlob(blob, `${filename}.csv`);
			} else {
				const blobs = await getBlobs(attachments);
				if (blobs.every((b) => b === null)) throw new Error("All file downloads failed");
				const zipFile = zipBlobs(blobs, attachments);
				await FileDownloader.downloadZip(zipFile, filename);
			}
		}
	}

	export async function downloadURL(url: string, filename: string = "", zip: boolean = false) {
		const blob = await getBlob(url);

		if (zip) {
			const zip = zipBlob(blob, filename);
			await FileDownloader.downloadZip(zip, filename);
		} else {
			FileDownloader.downloadBlob(blob, filename);
		}
	}

	export async function downloadZip(zip: JSZip, filename: string = "") {
		const content = await zip.generateAsync({ type: "blob" });
		if (!filename.endsWith(".zip")) filename += ".zip";
		FileDownloader.downloadBlob(content, filename);
	}

	export function downloadBlob(content: Blob, filename: string = DEFAULT_FILE_NAME) {
		const u = URL.createObjectURL(content);
		const a = document.createElement("a");
		a.href = u;
		a.download = filename;
		a.click();
		URL.revokeObjectURL(u);
	}

	export async function loadAttachment<T = ArrayBuffer | Blob | string>(
		attachment: IAirtableAttachment,
		recordId: string,
		responseType: FileResponseType,
	) {
		if (await AWS.S3.doesFileExist(attachment.filename, recordId)) {
			// first we will try to download the file from S3
			//  - because it's more trustworthy than Airtable
			//  - and because most of these files are already in S3
			return await AWS.S3.download<T>(attachment.filename, responseType, recordId);
		} else {
			// if that fails, we will try to download the attachment directly
			return await FileDownloader.loadURL<T>(attachment.url, responseType);
		}
	}

	export async function loadURL<T = ArrayBuffer | Blob | string>(url: string, responseType: FileResponseType) {
		const response = await axios.get<T>(url, { responseType });
		if (response.status === 200) return response.data;
	}
}

async function getBlobs(files: IAirtableAttachment[]) {
	return await Promise.all(
		files.map(async (file) => {
			try {
				return await getBlob(file.url);
			} catch {
				console.warn(`Failed to download file: ${file.filename}`);
				return null;
			}
		}),
	);
}

async function getBlob(url: string) {
	const response = await fetch(url);
	if (!response.ok) throw new Error(`Failed to download: ${response.status} ${response.statusText}`);
	return await response.blob();
}

function zipBlobs(blobs: (Blob | null)[], files: IAirtableAttachment[]) {
	const zip = new JSZip();
	blobs.forEach((blob, i) => {
		if (blob) {
			zip.file(files[i].filename, blob);
		}
	});
	return zip;
}

function zipBlob(blob: Blob, filename: string) {
	const zip = new JSZip();
	zip.file(filename, blob);
	return zip;
}
